import userConstants from '../user/constants';
import safeRedirectPath from 'app/client/utilities/safeRedirectPath';

/**
 * @type {Object} The initial state.
 */
export const initialState = {
  customer: null,
  canSwitchCustomer: false,
  redirectTo: null,
  validCustomer: true,
  sessionExpired: false,
  maintenance: false,

  // The translation key prefix of the error to show, containing a title and a message.
  error: null,

  // Visual.
  language: 'nl',
  theme: 'light',
  autoSwitchTheme: true,

  // Info.
  newVersion: false,
};

/**
 * @type {Object} constants used to modify the state.
 */
export const constants = {
  setLocked: 'APP/SET_LOCKED',
  setSessionExpired: 'APP/SET_SESSION_EXPIRED',
  invalidateCustomer: 'APP/INVALIDATE_CUSTOMER',
  clearRedirect: 'APP/CLEAR_REDIRECT',
  setMaintenance: 'APP/SET_MAINTENANCE',
  setTheme: 'APP/SET_THEME',
  setAutoSwitchTheme: 'APP/SET_AUTO_SWITCH_THEME',
  setLanguage: 'APP/SET_LANGUAGE',
  setCustomer: 'APP/SET_CUSTOMER',
  setNewVersion: 'APP/SET_NEW_VERSION',
  setError: 'APP/SET_ERROR',
};

// The visual preferences: the only `app` state a session that ends carries over,
// and the only state a stored setting may reach. A key that is not there is left
// out, so it keeps the value it has.
const visualOf = (source = {}) =>
  Object.fromEntries(
    ['theme', 'language', 'autoSwitchTheme']
      .filter((key) => source[key] !== undefined)
      .map((key) => [key, source[key]]),
  );

/**
 * A reducer to modify the state.
 *
 * @param {Object} state The current state.
 * @param {string} type The action type.
 * @param {*} payload The action payload.
 *
 * @returns {Object} The updated state.
 */
export default (state = initialState, { type, payload } = {}) => {
  switch (type) {
    case constants.setSessionExpired: {
      return {
        ...initialState,
        ...visualOf(state),
        sessionExpired: payload,
      };
    }

    // The customer scoped pages render straight from `app.customer`, so this
    // unmounts them before the store loses it.
    case constants.invalidateCustomer:
      return { ...state, validCustomer: false };

    // A target that outlives its transition redirects a later visit to an auth
    // route to the page before it.
    case constants.clearRedirect:
      return { ...state, redirectTo: null };

    case constants.setMaintenance:
      return { ...state, maintenance: payload };

    case constants.setLanguage:
      return {
        ...state,
        language: payload,
      };

    case constants.setTheme: {
      if (payload !== 'toggle') {
        return { ...state, theme: payload };
      }

      return {
        ...state,
        theme: state.theme === 'light' ? 'dark' : 'light',
      };
    }

    case constants.setAutoSwitchTheme: {
      return { ...state, autoSwitchTheme: payload };
    }

    case userConstants.login:
      return {
        ...state,
        ...visualOf(payload.settings),
        customer: payload.app.customer ?? null,
        canSwitchCustomer: payload.app.canSwitchCustomer === true,
        validCustomer: true,
        sessionExpired: false,
        error: null,
        // Sanitised here too: this ends up in a `Navigate`, where an unchecked
        // value is an open redirect.
        redirectTo: payload.redirectTo ? safeRedirectPath(payload.redirectTo) : null,
      };

    case userConstants.closeCustomer:
      return { ...state, customer: null, validCustomer: true, redirectTo: null, error: null };

    case userConstants.logout:
      return { ...initialState, ...visualOf(state) };

    case constants.setCustomer:
      return {
        ...state,
        customer: payload,
        validCustomer: true,
      };

    case constants.setNewVersion:
      return { ...state, newVersion: payload };

    case constants.setError:
      return { ...state, error: payload };

    default:
      return state;
  }
};
